/**
 * SystemHealthBanner
 *
 * A sticky, prominent banner shown at the top of the admin area when the
 * Claude/Anthropic API is failing for a durable reason (out of credits, or
 * an invalid key). It exists because a $0.10 unpaid Anthropic balance once
 * took down chat / research / scoring *silently* for weeks — every AI call
 * fails quietly and callers fall back to heuristics, so nobody noticed.
 *
 * It polls the PUBLIC `/system/status` endpoint on mount and every 60s.
 * The endpoint fails open (reports healthy) on any backend/Redis error, and
 * this component is deliberately resilient: if the fetch itself errors, it
 * renders nothing — a network blip should never scare the team with a false
 * alarm. Only an explicit `{ ok: false }` from the server shows the banner.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import api from '../services/api';

interface AiStatus {
  ok: boolean;
  reason?: string | null;
  detail?: string | null;
  since?: string | null;
}

const POLL_MS = 60_000;

const SystemHealthBanner: React.FC = () => {
  const [status, setStatus] = useState<AiStatus | null>(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const res = await api.get<AiStatus>('/system/status');
        if (!cancelled) {
          setStatus(res.data);
        }
      } catch {
        // Fetch failed (network / server blip). Do NOT show a banner on a
        // transient error — clear any stale degraded state instead so we
        // never leave a false alarm up.
        if (!cancelled) {
          setStatus(null);
        }
      }
    };

    check();
    const handle = window.setInterval(check, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, []);

  // Healthy, unknown, or fetch-errored → render nothing.
  if (!status || status.ok !== false) {
    return null;
  }

  return (
    <div
      role="alert"
      className="sticky top-0 z-50 w-full bg-amber-50 border-b border-amber-300 text-amber-900 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-3 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            AI services are currently unavailable.
          </p>
          <p className="text-sm">
            Research, chat, and scoring may not work right now. Please contact
            your administrator to check the platform.
          </p>
          {status.detail && (
            <p className="mt-0.5 text-xs text-amber-700">{status.detail}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemHealthBanner;
